import React, { createContext, useContext, useEffect, useState } from 'react'
import { User } from '@supabase/supabase-js'
import { createClient } from '@supabase/supabase-js'
import { supabase, UserProfile, UserRole } from '../lib/supabase'
import { WebAuthnService } from '../lib/webauthn-service'
import { WebAuthnUtils } from '../lib/webauthn-utils'
import type { WebAuthnError, PasskeyListItem } from '../types/webauthn'

interface AuthContextType {
  user: User | null
  userProfile: UserProfile | null
  loading: boolean
  signIn: (email: string, password: string) => Promise<{ error?: string }>
  signOut: () => Promise<void>
  signUp: (email: string, password: string, userData: any) => Promise<{ error?: string }>
  // Passkey methods
  signInWithPasskey: () => Promise<{ error?: string }>
  registerPasskey: (credentialName?: string) => Promise<{ error?: string }>
  getUserPasskeys: () => Promise<PasskeyListItem[]>
  deletePasskey: (credentialId: string) => Promise<{ error?: string }>
  isPasskeySupported: () => Promise<boolean>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null)
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null)
  const [loading, setLoading] = useState(true)
  const [passkeySupported, setPasskeySupported] = useState<boolean>(false)

  // Função simples para definir loading
  const setLoadingSimple = (value: boolean) => {
    setLoading(value)
  }

  // Função básica para carregar perfil do usuário
  const loadUserProfile = async (userId: string): Promise<UserProfile | null> => {
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', userId)
        .single()

      if (error || !data) {
        return null
      }
      
      return data as UserProfile
    } catch (error) {
      return null
    }
  }

  // Função de login básica
  const signIn = async (email: string, password: string) => {
    try {
      setLoadingSimple(true)

      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      })
      
      if (error) {
        setLoadingSimple(false)
        return { error: error.message }
      }

      if (data.user) {
        setUser(data.user)
        const profile = await loadUserProfile(data.user.id)
        setUserProfile(profile)
        setLoadingSimple(false)
      } else {
        setLoadingSimple(false)
      }
      
      return {}
    } catch (error: any) {
      setLoadingSimple(false)
      return { error: error.message || 'Erro inesperado' }
    }
  }

  // Função de logout básica
  const signOut = async () => {
    try {
      // Clear Supabase session
      await supabase.auth.signOut()
      
      // Clear persisted passkey session
      try {
        localStorage.removeItem('saudevirtual_passkey_session')
        console.log('🧹 Sessão de passkey removida do localStorage');
      } catch (storageError) {
        console.warn('⚠️ Erro ao limpar sessão persistida:', storageError);
      }
      
      setUser(null)
      setUserProfile(null)
      setLoading(false)
      window.location.href = '/home'
    } catch (error) {
      setUser(null)
      setUserProfile(null)
      setLoading(false)
      window.location.href = '/home'
    }
  }

  // Função de registro básica
  const signUp = async (email: string, password: string, userData: any) => {
    try {
      setLoadingSimple(true)
      console.log('🚀 Iniciando processo de registro para:', email);

      // Usar método tradicional de signUp (sem triggers)
      console.log('👤 Criando usuário...');
      const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: userData // Incluir dados no user_metadata
        }
      });

      if (signUpError) {
        console.error('❌ Erro ao criar usuário:', signUpError);
        setLoadingSimple(false)
        return { error: signUpError.message }
      }

      if (!signUpData.user) {
        setLoadingSimple(false)
        return { error: 'Falha ao criar usuário' }
      }

      console.log('✅ Usuário criado com sucesso:', signUpData.user.id);
      
      // Aguardar um pouco para garantir que o usuário foi criado
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Criar perfil manualmente usando anon key (com RLS desabilitado)
      console.log('📝 Criando perfil do usuário...');
      
      const profileData = {
        id: signUpData.user.id,
        email: email,
        full_name: userData.full_name || email,
        role: userData.role || 'patient',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        ...(userData.role === 'doctor' && {
          medical_license: userData.medical_license || null,
          specialization: userData.specialization || null,
          hospital_affiliation: userData.hospital_affiliation || null
        }),
        ...(userData.role === 'patient' && {
          date_of_birth: userData.date_of_birth || null,
          gender: userData.gender || null,
          phone: userData.phone || null
        })
      };
      
      // Tentar criar perfil com cliente normal primeiro
      let profileError = null;
      try {
        const { error } = await supabase
          .from('user_profiles')
          .insert([profileData]);
        profileError = error;
      } catch (insertError: any) {
        profileError = insertError;
      }
      
      if (profileError) {
        console.warn('⚠️ Erro ao criar perfil com anon key:', profileError.message);
        console.log('🔄 Tentando com service role...');
        
        // Fallback: tentar com service role se disponível
        try {
          const serviceRoleClient = createClient(
            import.meta.env.VITE_SUPABASE_URL,
            import.meta.env.VITE_SERVICE_ROLE_KEY || ''
          );
          
          const { error: serviceError } = await serviceRoleClient
            .from('user_profiles')
            .insert([profileData]);
          
          if (serviceError) {
            console.error('❌ Erro ao criar perfil com service role:', serviceError);
            setLoadingSimple(false)
            return { error: 'Erro ao criar perfil do usuário' }
          }
          
          console.log('✅ Perfil criado com service role!');
        } catch (serviceRoleError) {
          console.error('❌ Service role não disponível:', serviceRoleError);
          setLoadingSimple(false)
          return { error: 'Erro ao criar perfil do usuário' }
        }
      } else {
        console.log('✅ Perfil criado com anon key!');
      }
      
      console.log('🎉 Registro completo para:', email);

      setLoadingSimple(false)
      return {}
    } catch (error: any) {
      console.error('❌ Erro inesperado no registro:', error);
      setLoadingSimple(false)
      return { error: error.message || 'Erro inesperado ao criar conta' }
    }
  }

  // Passkey Methods
  
  // Check if passkeys are supported
  const isPasskeySupported = async (): Promise<boolean> => {
    try {
      const support = await WebAuthnUtils.WebAuthnDetector.checkSupport()
      setPasskeySupported(support.available)
      return support.available
    } catch (error) {
      console.error('Error checking passkey support:', error)
      setPasskeySupported(false)
      return false
    }
  }

  // Sign in with passkey
  const signInWithPasskey = async (): Promise<{ error?: string }> => {
    try {
      console.log('🔐 Iniciando login com passkey...');
      setLoadingSimple(true)

      // Verificar se WebAuthn é suportado
      const isSupported = await isPasskeySupported();
      if (!isSupported) {
        setLoadingSimple(false);
        return { error: 'Passkeys não são suportados neste navegador.' };
      }

      console.log('🌐 Permitindo seleção de qualquer passkey disponível...');

      // Tentar autenticação com retry
      let lastError: any = null;
      const maxRetries = 2;
      
      for (let attempt = 1; attempt <= maxRetries; attempt++) {
        try {
          console.log(`🔄 Tentativa ${attempt}/${maxRetries} de autenticação...`);
          
          if (attempt > 1) {
            // Delay entre tentativas
            await new Promise(resolve => setTimeout(resolve, 2000));
            console.log('⏳ Aguardando antes da próxima tentativa...');
          }

          // Start authentication (sem restrições de credenciais)
          const requestOptions = await WebAuthnService.startAuthentication()

          // Get credential from browser
          const credential = await navigator.credentials.get({
            publicKey: requestOptions
          }) as PublicKeyCredential

          if (!credential) {
            throw new Error('Falha ao obter credencial')
          }

          console.log('✅ Credencial obtida do navegador');

          // Complete authentication
          const result = await WebAuthnService.completeAuthentication(credential)
          
          console.log('✅ Autenticação WebAuthn concluída para usuário:', result.user_id);

          // After successful passkey verification, establish persistent session
          console.log('✅ Passkey verificado com sucesso, estabelecendo sessão persistente...');
          
          // Get user data from database using the verified user_id
          const { data: userData, error: userDataError } = await supabase
            .from('user_profiles')
            .select('*')
            .eq('id', result.user_id)
            .single()
          
          if (userDataError || !userData) {
            console.error('❌ Erro ao buscar dados do usuário:', userDataError);
            setLoadingSimple(false)
            return { error: 'Usuário não encontrado no sistema.' }
          }
          
          // Create session data with proper structure matching Supabase traditional login
          const sessionData = {
            user: {
              id: result.user_id,
              email: userData.email,
              user_metadata: {
                date_of_birth: userData.date_of_birth,
                email: userData.email,
                email_verified: true,
                full_name: userData.full_name,
                gender: userData.gender,
                phone: userData.phone,
                role: userData.role,
                hospital_affiliation: userData.hospital_affiliation,
                medical_license: userData.medical_license,
                specialization: userData.specialization
              },
              app_metadata: {
                provider: 'passkey',
                providers: ['passkey']
              },
              aud: 'authenticated',
              created_at: userData.created_at,
              updated_at: userData.updated_at
            },
            profile: userData,
            authMethod: 'passkey',
            timestamp: Date.now(),
            expiresAt: Date.now() + (24 * 60 * 60 * 1000) // 24 hours
          }
          
          // Store session in localStorage for persistence
          try {
            localStorage.setItem('saudevirtual_passkey_session', JSON.stringify(sessionData))
            console.log('💾 Sessão persistida no localStorage');
          } catch (storageError) {
            console.warn('⚠️ Erro ao persistir sessão:', storageError);
          }
          
          // Set user and profile in context
          setUser(sessionData.user as any)
          setUserProfile(sessionData.profile)
          setLoadingSimple(false)
          
          // Debug: Log user object structure for comparison
          console.log('🔍 [DEBUG] Objeto user criado via passkey:', {
            id: sessionData.user.id,
            email: sessionData.user.email,
            aud: sessionData.user.aud,
            user_metadata: sessionData.user.user_metadata,
            app_metadata: sessionData.user.app_metadata
          });
          console.log('🔍 [DEBUG] Perfil do usuário via passkey:', userData);
          
          console.log('🎉 Sessão estabelecida via passkey para:', userData.email);
          return {}

          console.log('🎉 Login com passkey realizado com sucesso!');
          return {}
        } catch (attemptError: any) {
          lastError = attemptError;
          console.warn(`❌ Tentativa ${attempt} falhou:`, attemptError.message);
          
          // Verificar se deve tentar novamente
          const shouldRetry = attempt < maxRetries && (
            attemptError.name === 'NotAllowedError' ||
            attemptError.name === 'TimeoutError' ||
            attemptError.name === 'AbortError'
          );
          
          if (!shouldRetry) {
            break;
          }
          
          console.log(`🔄 Tentando novamente em 2 segundos... (${maxRetries - attempt} tentativas restantes)`);
        }
      }
      
      // Se chegou aqui, todas as tentativas falharam
      throw lastError;
    } catch (error: any) {
      console.error('❌ Erro no login com passkey:', error)
      setLoadingSimple(false)
      const webauthnError = WebAuthnUtils.WebAuthnErrorHandler.handleError(error)
      
      // Mensagens de erro mais específicas
      let errorMessage = webauthnError.message;
      if (error.name === 'NotAllowedError') {
        errorMessage = 'Operação cancelada ou não permitida. Tente novamente.';
      } else if (error.name === 'TimeoutError') {
        errorMessage = 'Tempo limite excedido. Tente novamente.';
      } else if (error.name === 'AbortError') {
        errorMessage = 'Operação cancelada. Tente novamente.';
      } else if (error.name === 'NotSupportedError') {
        errorMessage = 'Passkeys não são suportados neste navegador.';
      }
      
      return { error: errorMessage }
    }
  }

  // Register a new passkey
  const registerPasskey = async (credentialName?: string): Promise<{ error?: string }> => {
    if (!user) {
      return { error: 'Usuário deve estar logado para registrar passkey' }
    }

    try {
      setLoadingSimple(true)

      // Start registration
      const creationOptions = await WebAuthnService.startRegistration({
        user_id: user.id,
        username: user.email || `user_${user.id}`,
        display_name: userProfile?.full_name || user.email || 'Saúde Virtual User',
        credential_name: credentialName || 'Novo Passkey',
        authenticator_attachment: 'platform',
        user_verification: 'preferred',
        resident_key: 'preferred'
      })

      // Create credential
      const credential = await navigator.credentials.create({
        publicKey: creationOptions
      }) as PublicKeyCredential

      if (!credential) {
        setLoadingSimple(false)
        return { error: 'Falha ao criar credencial' }
      }

      // Complete registration
      await WebAuthnService.completeRegistration(
        user.id,
        credential,
        credentialName
      )

      setLoadingSimple(false)
      return {}
    } catch (error: any) {
      console.error('Passkey registration error:', error)
      setLoadingSimple(false)
      const webauthnError = WebAuthnUtils.WebAuthnErrorHandler.handleError(error)
      return { error: webauthnError.message }
    }
  }

  // Get user's passkeys
  const getUserPasskeys = async (): Promise<PasskeyListItem[]> => {
    if (!user) {
      return []
    }

    try {
      return await WebAuthnService.getUserCredentials(user.id)
    } catch (error) {
      console.error('Error fetching user passkeys:', error)
      return []
    }
  }

  // Delete a passkey
  const deletePasskey = async (credentialId: string): Promise<{ error?: string }> => {
    if (!user) {
      return { error: 'Usuário deve estar logado para deletar passkey' }
    }

    try {
      await WebAuthnService.deleteCredential(credentialId, user.id)
      return {}
    } catch (error: any) {
      console.error('Error deleting passkey:', error)
      const webauthnError = WebAuthnUtils.WebAuthnErrorHandler.handleError(error)
      return { error: webauthnError.message }
    }
  }

  // Inicialização com timeout e tratamento robusto
  useEffect(() => {
    let timeoutId: NodeJS.Timeout
    let isInitialized = false
    let retryCount = 0
    const maxRetries = 2

    // Detectar ambiente do Trae preview browser
    const isTraePreview = () => {
      const userAgent = navigator.userAgent.toLowerCase()
      const isEmbedded = window.parent !== window
      const hasTraeIndicators = userAgent.includes('trae') || 
                               userAgent.includes('electron') ||
                               document.referrer.includes('trae') ||
                               window.location.hostname === 'localhost'
      
      console.log('🔍 Detecção de ambiente:', {
        userAgent: userAgent.substring(0, 50) + '...',
        isEmbedded,
        hasTraeIndicators,
        hostname: window.location.hostname
      })
      
      return isEmbedded || hasTraeIndicators
    }

    const initializeAuth = async (attempt = 1) => {
      try {
        console.log(`🔐 Iniciando verificação de autenticação (tentativa ${attempt}/${maxRetries + 1})...`)
        
        // First, check for persisted passkey session
        const checkPasskeySession = () => {
          try {
            const storedSession = localStorage.getItem('saudevirtual_passkey_session')
            if (storedSession) {
              const sessionData = JSON.parse(storedSession)
              
              // Check if session is still valid
              if (sessionData.expiresAt && Date.now() < sessionData.expiresAt) {
                console.log('✅ Sessão de passkey válida encontrada no localStorage');
                return sessionData
              } else {
                console.log('⏰ Sessão de passkey expirada, removendo...');
                localStorage.removeItem('saudevirtual_passkey_session')
              }
            }
          } catch (error) {
            console.warn('⚠️ Erro ao verificar sessão persistida:', error);
            localStorage.removeItem('saudevirtual_passkey_session')
          }
          return null
        }
        
        // Check for passkey session first
        const passkeySession = checkPasskeySession()
        if (passkeySession && !isInitialized) {
          console.log('🔑 Restaurando sessão de passkey:', passkeySession.user.email);
          setUser(passkeySession.user)
          setUserProfile(passkeySession.profile)
          isInitialized = true
          setLoadingSimple(false)
          return
        }
        
        // Timeout adaptativo baseado no ambiente
        const isPreview = isTraePreview()
        const timeoutDuration = isPreview ? 8000 : 5000 // 8s para Trae preview, 5s para outros
        
        console.log(`⏱️ Usando timeout de ${timeoutDuration}ms (ambiente: ${isPreview ? 'Trae Preview' : 'Browser Normal'})`)
        
        // Timeout de segurança para evitar travamento
        const timeoutPromise = new Promise((_, reject) => {
          timeoutId = setTimeout(() => {
            reject(new Error('Timeout na verificação de autenticação'))
          }, timeoutDuration)
        })

        // Promise da verificação de sessão Supabase
        const sessionPromise = supabase.auth.getSession()

        // Race entre timeout e verificação
        const { data: { session } } = await Promise.race([
          sessionPromise,
          timeoutPromise
        ]) as any
        
        if (!isInitialized) {
          clearTimeout(timeoutId)
          
          if (session?.user) {
            console.log('✅ Usuário autenticado encontrado:', session.user.email)
            
            // Debug: Log user object structure for comparison
            console.log('🔍 [DEBUG] Objeto user do Supabase tradicional:', {
              id: session.user.id,
              email: session.user.email,
              aud: session.user.aud,
              user_metadata: session.user.user_metadata,
              app_metadata: session.user.app_metadata
            });
            
            setUser(session.user)
            
            try {
              const profile = await loadUserProfile(session.user.id)
              setUserProfile(profile)
              console.log('✅ Perfil carregado:', profile?.role)
              console.log('🔍 [DEBUG] Perfil do usuário via Supabase:', profile);
            } catch (profileError) {
              console.warn('⚠️ Erro ao carregar perfil:', profileError)
              // Continua mesmo se o perfil falhar
            }
          } else {
            console.log('ℹ️ Nenhuma sessão ativa encontrada')
          }
          
          isInitialized = true
          setLoadingSimple(false)
        }
      } catch (error: any) {
        console.error(`❌ Erro na inicialização de autenticação (tentativa ${attempt}):`, error.message)
        
        // Implementar retry automático
        if (attempt <= maxRetries && !isInitialized) {
          console.log(`🔄 Tentando novamente em 1 segundo... (${attempt}/${maxRetries})`);
          setTimeout(() => {
            if (!isInitialized) {
              initializeAuth(attempt + 1);
            }
          }, 1000);
          return;
        }
        
        if (!isInitialized) {
          clearTimeout(timeoutId)
          
          // Fallback após esgotar tentativas
          try {
            console.log('🔄 Implementando fallback robusto...');
            // Verificar se há dados de sessão válidos no Supabase
            const { data } = await supabase.auth.getUser();
            if (data?.user && !isInitialized) {
              console.log('✅ Usuário recuperado via fallback');
              isInitialized = true;
              setUser(data.user);
              const profile = await loadUserProfile(data.user.id);
              setUserProfile(profile);
              setLoadingSimple(false);
              return;
            }
          } catch (fallbackError) {
            console.warn('⚠️ Erro no fallback:', fallbackError);
          }
          
          // Garantir que sempre saia do loading
          console.log('🔄 Finalizando inicialização com estado limpo');
          isInitialized = true
          setUser(null)
          setUserProfile(null)
          setLoadingSimple(false)
        }
      }
    }
    
    // Timeout adicional de segurança absoluta (adaptativo)
    const isPreviewEnv = isTraePreview()
    const absoluteTimeoutDuration = isPreviewEnv ? 12000 : 7000 // 12s para Trae preview, 7s para outros
    
    console.log(`⏱️ Timeout absoluto configurado para ${absoluteTimeoutDuration}ms`)
    
    const absoluteTimeout = setTimeout(() => {
      if (!isInitialized) {
        console.warn('⚠️ Timeout absoluto atingido - forçando saída do loading após todas as tentativas')
        console.log('🔄 Estado final: usuário não autenticado')
        isInitialized = true
        setUser(null)
        setUserProfile(null)
        setLoadingSimple(false)
      }
    }, absoluteTimeoutDuration)

    // Iniciar processo de autenticação
    console.log('🚀 Iniciando processo de autenticação...')
    initializeAuth()
    
    // Cleanup
    return () => {
      clearTimeout(timeoutId)
      clearTimeout(absoluteTimeout)
      isInitialized = true
    }

    // Listener para mudanças de autenticação (separado da inicialização)
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        console.log('🔄 Evento de autenticação:', event)
        
        // Só processar eventos se já inicializou
        if (isInitialized) {
          if (event === 'SIGNED_IN' && session?.user) {
            console.log('✅ Login detectado:', session.user.email)
            setUser(session.user)
            
            try {
              const profile = await loadUserProfile(session.user.id)
              setUserProfile(profile)
            } catch (error) {
              console.warn('⚠️ Erro ao carregar perfil no login:', error)
            }
            
            setLoadingSimple(false)
          } else if (event === 'SIGNED_OUT') {
            console.log('🚪 Logout detectado')
            setUser(null)
            setUserProfile(null)
            setLoading(false)
          }
        }
      }
    )

    return () => {
      subscription.unsubscribe()
      clearTimeout(timeoutId)
      clearTimeout(absoluteTimeout)
    }
  }, [])

  // Check passkey support on mount
  useEffect(() => {
    isPasskeySupported()
  }, [])

  const value = {
    user,
    userProfile,
    loading,
    signIn,
    signOut,
    signUp,
    signInWithPasskey,
    registerPasskey,
    getUserPasskeys,
    deletePasskey,
    isPasskeySupported
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

// Hook para usar o contexto de autenticação
export const useAuth = () => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export default AuthProvider
export { AuthContext }